import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { theme as antdTheme } from 'antd';
import dayjs from 'dayjs';
import styled from 'styled-components';

import chevronIcon from '../../../assets/chevronIcon.svg';
import protectIcon from '../../../assets/protectIcon.svg';
import deleteIcon from '../../../assets/registrantDelete.svg';
import editIcon from '../../../assets/registrantEdit.svg';
import { useVismaConfiguration } from '../../../services/visitor-management';
import type { Registrant as RegistrantType } from '../../../types';
import { useRegisterPage } from '../register-page-context';
import DeleteRegistrantConfirmModal from './delete-registrant-confirm-modal';

const { screenSMMax } = antdTheme.getDesignToken();

const Container = styled.div`
  background: ${({ theme }) => theme.white};
  margin-bottom: 16px;
  border-radius: 10px;
  padding: 20px;
  overflow: hidden;

  @media only screen and (max-width: ${screenSMMax}px) {
    padding: 8px;
    margin-bottom: 8px;
  }
`;

const FirstRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas: 'icon name actions';
  column-gap: 12px;
  row-gap: 6px;
  align-items: center;
`;

const Name = styled.h4`
  font-weight: 600;
  font-size: 20px;
  color: ${({ theme }) => theme.text};
  word-break: break-word;

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 16px;
  }
`;

const NameContainer = styled.div`
  grid-area: name;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
`;

const Dot = styled.span`
  margin: 0 4px;
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 400;
  font-size: 16px;
`;

const CompanyName = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 13px;
  }
`;

interface ExpiryContainerProps {
  isExpanded?: boolean;
}

const ExpiryContainer = styled.div<ExpiryContainerProps>`
  display: flex;
  align-items: flex-start;
  margin-top: 6px;
  padding-bottom: ${({ isExpanded }) => (isExpanded ? '12px' : '0')};
  border-bottom: ${({ theme, isExpanded }) =>
    isExpanded ? `1px solid ${theme.textSecondary}22` : 'none'};
`;

const ExpiryText = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.textSecondary};
  margin-left: 8px;

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 13px;
  }
`;

const RightContainer = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 12px;
  align-self: center;

  @media only screen and (max-width: ${screenSMMax}px) {
    gap: 6px;
  }

  & > * {
    cursor: pointer;
    transition: 0.5s;

    &:hover {
      opacity: 0.8;
      transition: 0.5s;
    }
  }
`;

const IconContainer = styled.div`
  grid-area: icon;
  align-self: center;
`;

const IconBackground = styled.div`
  min-height: 32px;
  max-height: 32px;
  min-width: 32px;
  max-width: 32px;

  @media only screen and (max-width: ${screenSMMax}px) {
    min-height: 28px;
    max-height: 28px;
    min-width: 28px;
    max-width: 28px;
  }
  background: ${({ theme }) => theme.backgroundInput};
  align-items: center;
  display: flex;
  justify-content: center;
  border-radius: 6px;
`;

const ExpandContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-top: 16px;

  @media only screen and (max-width: ${screenSMMax}px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const Expand = styled.div``;

const ExpandText = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};

  @media only screen and (max-width: ${screenSMMax}px) {
    font-size: 14px;
  }
`;

const ExpandTitle = styled.h6`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary};
`;

interface RegistrantProps {
  registrant: RegistrantType;
}

function Registrant({ registrant }: RegistrantProps) {
  const intl = useIntl();
  const { setEditRegistrantId, removeRegistrant, setRegistrantInfo } =
    useRegisterPage()!;
  const { configuration } = useVismaConfiguration();
  const dpOptions = configuration?.dataProtection?.expiryOptions ?? [];
  const dataField = configuration?.personalData?.data_field;
  const expiryLabel =
    dpOptions.find((o) => o.value === registrant.expiry)?.text ?? '';
  const [deleteModal, setDeleteModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleDelete = () => {
    setDeleteModal(true);
  };

  const handleEdit = () => {
    setRegistrantInfo(registrant);
    setEditRegistrantId(registrant.docNo);
  };

  return (
    <Container>
      <DeleteRegistrantConfirmModal
        open={deleteModal}
        closeModal={() => setDeleteModal(false)}
        deleteRegistrant={() => removeRegistrant(registrant.docNo)}
      />
      <FirstRow>
        <IconContainer>
          <IconBackground onClick={() => setIsExpanded(!isExpanded)}>
            <img
              src={chevronIcon}
              alt="chevron"
              style={{
                userSelect: 'none',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
          </IconBackground>
        </IconContainer>
        <NameContainer>
          <Name>{registrant.fullname}</Name>
          {registrant.companyName && !dataField?.company_name?.disabled && (
            <>
              <Dot>·</Dot>
              <CompanyName>{registrant.companyName}</CompanyName>
            </>
          )}
        </NameContainer>
        <RightContainer>
          <img src={deleteIcon} onClick={handleDelete} alt="delete" />
          <img src={editIcon} onClick={handleEdit} alt="edit" />
        </RightContainer>
      </FirstRow>
      <ExpiryContainer isExpanded={isExpanded}>
        <img src={protectIcon} alt="protect" />
        <ExpiryText>{expiryLabel}</ExpiryText>
      </ExpiryContainer>
      {isExpanded && (
        <ExpandContainer>
          <Expand>
            <ExpandTitle>
              {intl.formatMessage({ id: 'DATE_OF_BIRTH' })}
            </ExpandTitle>
            <ExpandText>
              {dayjs(registrant?.dateOfBirth)?.format('DD/MMM/YYYY') || ''}
            </ExpandText>
          </Expand>
          <Expand>
            <ExpandTitle>{intl.formatMessage({ id: 'DOC_NO' })}</ExpandTitle>
            <ExpandText>{registrant.docNo}</ExpandText>
          </Expand>
          <Expand>
            <ExpandTitle>
              {intl.formatMessage({ id: 'DOC_EXPIRY_DATE' })}
            </ExpandTitle>
            <ExpandText>
              {dayjs(registrant?.docExpiryDate)?.format('DD/MMM/YYYY') || ''}
            </ExpandText>
          </Expand>
        </ExpandContainer>
      )}
    </Container>
  );
}

export default Registrant;
